import React, { useEffect, useMemo, useState } from "react";
import "../fonts/breuer-headline.css";
import "./thankyou.css";
import { useDispatch, useSelector } from "react-redux";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";

import thankYouBg from "./img/thankyou-bg.jpg";
import thankYouBgMobile from "./img/thankyou-bg-mobile.png";
import thankYouGif from "./img/thankyou-gif.gif";

const MOBILE_PORTRAIT_MQ = "(max-width: 768px) and (orientation: portrait)";

/** Mirrors the other pages' mobile-portrait breakpoint, used to pick ty_mob.png. */
function useMobilePortrait() {
  const [isMobilePortrait, setIsMobilePortrait] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(MOBILE_PORTRAIT_MQ).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(MOBILE_PORTRAIT_MQ);
    const sync = () => setIsMobilePortrait(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, []);

  return isMobilePortrait;
}

// old/thankyou.php's "Your Score is X out of 18 in Y minutes." — 18 is the
// mind-palace room's max score (3 statements + 4 anagram + 1 quiz +
// 6 wordcloud + 4 word-search positive words, see game.jsx).
const MAX_SCORE = 18;

// game.jsx stores elapsed time as "MM:SS" (e.g. "15:23") — shown as-is,
// e.g. "15:23 minutes".
function formatDurationLabel(clock) {
  const trimmed = String(clock ?? "").trim();
  return trimmed ? `${trimmed} minutes` : null;
}

function readStoredUser(user) {
  try {
    const fromSession = JSON.parse(sessionStorage.getItem("userData") || "{}");
    if (
      fromSession &&
      typeof fromSession === "object" &&
      (fromSession.userId || fromSession.userid || fromSession.id)
    ) {
      return fromSession;
    }
  } catch {
    /* ignore */
  }
  return user && typeof user === "object" ? user : {};
}

function readThankYouUserId(stored) {
  const id = stored?.userId ?? stored?.userid ?? stored?.id;
  return id != null && String(id).trim() !== "" ? String(id).trim() : "";
}

export default function ThankYou() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const isMobilePortrait = useMobilePortrait();

  const backendBase = useMemo(
    () => String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, ""),
    []
  );

  const storedUser = useMemo(() => readStoredUser(user), [user]);
  const viewerId = useMemo(
    () => readThankYouUserId(storedUser),
    [storedUser]
  );
  const sessionId = storedUser?.sessionId;
  const organizationId = storedUser?.organizationId;

  const [score, setScore] = useState(null);
  const [timeClock, setTimeClock] = useState(null);

  useEffect(() => {
    if (user?.backButtonRedirect != null) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [user, dispatch]);

  // The game has ended — trap the browser back button here instead of
  // letting it walk back into a finished /game.
  useEffect(() => {
    window.history.pushState(null, "", window.location.href);
    const blockBack = () => {
      window.history.pushState(null, "", window.location.href);
    };
    window.addEventListener("popstate", blockBack);
    return () => window.removeEventListener("popstate", blockBack);
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };
    body.classList.remove("common-bg");
    body.classList.add("thank-you-scroll");
    html.style.overflow = "auto";
    body.style.overflow = "auto";
    body.style.backgroundImage = `url("${isMobilePortrait ? thankYouBgMobile : thankYouBg}")`;
    body.style.backgroundSize = "cover";
    body.style.backgroundRepeat = "no-repeat";
    body.style.backgroundPosition = "center center";
    body.style.backgroundAttachment = "fixed";
    return () => {
      body.classList.remove("thank-you-scroll");
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, [isMobilePortrait]);

  useEffect(() => {
    if (!viewerId || !adminToken || !backendBase || !sessionId || !organizationId) {
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({ userId: viewerId, sessionId, organizationId }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !data.success || !data.report) return;
        const r = data.report;
        if (r.points != null) setScore(Number(r.points) || 0);
        if (r.time != null && String(r.time).trim() !== "") {
          setTimeClock(String(r.time).trim());
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [viewerId, adminToken, backendBase, sessionId, organizationId]);

  return (
    <div className="thank-you-page">
      <img src={thankYouGif} className="thank-you-gif" alt="Escape To Inner Peace" />

      <div className="thank-you-score-block">
        {score != null && (
          <div className="thank-you-score-line">
            Your Score is {score} out of {MAX_SCORE}
            {timeClock ? ` in ${formatDurationLabel(timeClock)}.` : "."}
          </div>
        )}
        <div className="thank-you-message-block">
          <div className="thank-you-message-title">
            You&rsquo;ve Unlocked the Path to Self-Love &ndash; Now What&rsquo;s Next?
          </div>
          <p className="thank-you-message-line">
            Self-care, like the escape room, is a journey that needs daily effort. Shift
            your perspective to break free from stress.
          </p>
          <p className="thank-you-message-line">
            Make mindful choices by asking, &ldquo;Is this helping me?&rdquo; Balance your
            energy - give, but don&rsquo;t forget to recharge. Small actions matter - one
            breath, one pause, one kind thought can change everything.
          </p>
          <p className="thank-you-message-line">
            Commit to one self-care action today. Choosing yourself makes you stronger
            every day.
          </p>
        </div>
      </div>
    </div>
  );
}
